/**
 * Profit / Loss PDF, drawn directly with jsPDF + autoTable from the report payload.
 * Colors follow the site primary theme (see `utils/pdfTheme`).
 * @module profitLossPdf
 */

import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { format } from "date-fns";
import {
  PDF_MARGIN,
  drawKpiCards,
  drawMetricSections,
  drawPageFooters,
  drawTableSection,
  getPdfTheme,
} from "../../utils/pdfTheme";
import {
  buildProfitLossKpis,
  buildProfitLossMetricSections,
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  formatPeriod,
} from "./profitLossFormatters";

applyPlugin(jsPDF);

/**
 * Low-ink header: no fill, gray text, and a thin gray rule underneath.
 * @param {jsPDF} pdf
 * @param {object} params
 * @returns {number} y below the header
 */
const drawHeader = (pdf, { report, reportName, parentCompany, theme }) => {
  const pageWidth = pdf.internal.pageSize.getWidth();

  pdf.setTextColor(...theme.text);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text(parentCompany?.name || "Company", PDF_MARGIN.left, 13);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...theme.muted);
  pdf.text(reportName, PDF_MARGIN.left, 19);

  pdf.setDrawColor(...theme.border);
  pdf.setLineWidth(0.4);
  pdf.line(PDF_MARGIN.left, 24, pageWidth - PDF_MARGIN.right, 24);

  pdf.setFontSize(8);
  const right = pageWidth - PDF_MARGIN.right;
  pdf.setTextColor(...theme.text);
  pdf.setFont("helvetica", "bold");
  pdf.text(formatPeriod(report?.period), right, 9, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(...theme.muted);
  pdf.text(
    `${report?.filters?.invoiceStatus === "paid" ? "Paid invoices" : "All invoices"} / ${
      report?.filters?.payoutStatus === "completed" ? "Completed payouts" : "All payouts"
    }`,
    right,
    14,
    { align: "right" }
  );
  pdf.text(
    `Generated ${format(new Date(report?.generatedAt || Date.now()), "MMM d, yyyy h:mm a")}`,
    right,
    19,
    { align: "right" }
  );

  pdf.setTextColor(...theme.text);
  return 31;
};

/**
 * Builds the Profit / Loss PDF document.
 *
 * @param {object} params
 * @param {object} params.report - Profit / Loss response from the reports API
 * @param {string} [params.reportName="Profit / Loss Report"]
 * @param {{ name?: string }} [params.parentCompany]
 * @returns {jsPDF}
 */
export const buildProfitLossPdf = ({
  report,
  reportName = "Profit / Loss Report",
  parentCompany = {},
}) => {
  const pdf = new jsPDF();
  const theme = getPdfTheme();
  const summary = report?.summary || {};
  const statement = report?.data || [];

  let y = drawHeader(pdf, { report, reportName, parentCompany, theme });

  if (report?.filters?.include?.expenses && !report?.expensesAvailable) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...theme.muted);
    pdf.text(
      "Expense tracking isn't set up yet; expenses are $0 and net profit equals gross profit.",
      PDF_MARGIN.left,
      y
    );
    y += 5;
  }

  y = drawKpiCards(pdf, buildProfitLossKpis(summary), y, theme);

  y = drawTableSection(pdf, {
    title: "Statement",
    head: ["Line", "Amount", "% of revenue"],
    body: statement.map((row) => [
      row.label,
      formatMoney(row.amount),
      formatPercent(row.percentOfRevenue),
    ]),
    y,
    theme,
    options: {
      alternateRowStyles: {},
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { halign: "right", cellWidth: 40 },
        2: { halign: "right", cellWidth: 30 },
      },
      didParseCell: (hook) => {
        if (hook.section !== "body") return;
        const row = statement[hook.row.index];
        if (!row) return;
        if (row.kind === "line" && hook.column.index === 0) {
          hook.cell.styles.cellPadding = { top: 1.6, bottom: 1.6, left: 6, right: 1.6 };
        }
        if (row.kind === "subtotal") {
          hook.cell.styles.fontStyle = "bold";
          hook.cell.styles.fillColor = theme.primaryTint;
        }
        if (row.kind === "total") {
          hook.cell.styles.fontStyle = "bold";
          hook.cell.styles.fillColor = theme.primary;
          hook.cell.styles.textColor = theme.white;
        }
        if (row.amount < 0 && hook.column.index === 1 && row.kind !== "total") {
          hook.cell.styles.textColor = theme.negative;
        }
      },
    },
  });

  y = drawMetricSections(pdf, buildProfitLossMetricSections(summary), y, theme);

  y = drawTableSection(pdf, {
    title: "By month",
    head: ["Month", "Revenue", "Labor", "Expenses", "Gross profit", "Net profit", "Net margin"],
    body: (report?.byMonth || []).map((row) => [
      formatMonth(row.month),
      formatMoney(row.revenue),
      formatMoney(row.laborCost),
      formatMoney(row.expenseCost),
      formatMoney(row.grossProfit),
      formatMoney(row.netProfit),
      formatPercent(row.netMargin),
    ]),
    y,
    theme,
    options: {
      columnStyles: Object.fromEntries(
        [1, 2, 3, 4, 5, 6].map((index) => [index, { halign: "right" }])
      ),
    },
  });

  y = drawTableSection(pdf, {
    title: "Revenue by client",
    head: ["Client", "Invoices", "Revenue", "% of revenue", "Outstanding"],
    body: (report?.byClient || []).map((row) => [
      row.clientName,
      formatNumber(row.invoiceCount),
      formatMoney(row.revenue),
      formatPercent(row.percentOfRevenue),
      formatMoney(row.outstanding),
    ]),
    y,
    theme,
    options: {
      columnStyles: Object.fromEntries([1, 2, 3, 4].map((i) => [i, { halign: "right" }])),
    },
  });

  y = drawTableSection(pdf, {
    title: "Labor by employee",
    head: ["Employee", "Payouts", "Total", "% of labor", "Miles", "Hours"],
    body: (report?.byEmployee || []).map((row) => [
      row.employeeName,
      formatNumber(row.payoutCount),
      formatMoney(row.total),
      formatPercent(row.percentOfLabor),
      formatNumber(row.miles),
      formatNumber(row.hours),
    ]),
    y,
    theme,
    options: {
      columnStyles: Object.fromEntries([1, 2, 3, 4, 5].map((i) => [i, { halign: "right" }])),
    },
  });

  drawTableSection(pdf, {
    title: "Expenses by category",
    head: ["Category", "Count", "Amount", "% of expenses"],
    body: (report?.byExpenseCategory || []).map((row) => [
      row.category,
      formatNumber(row.count),
      formatMoney(row.amount),
      formatPercent(row.percentOfExpenses),
    ]),
    y,
    theme,
    options: {
      columnStyles: Object.fromEntries([1, 2, 3].map((i) => [i, { halign: "right" }])),
    },
  });

  drawPageFooters(pdf, `${parentCompany?.name || "Company"}  |  ${reportName}`, theme);
  return pdf;
};

/**
 * Builds and downloads the Profit / Loss PDF.
 * @param {object} params - Same as `buildProfitLossPdf`
 */
export const downloadProfitLossPdf = (params) => {
  const pdf = buildProfitLossPdf(params);
  const name = (params.reportName || "profit-loss-report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  pdf.save(`${name}-${format(new Date(), "yyyy-MM-dd")}.pdf`);
};

export default downloadProfitLossPdf;
